import { Controller } from "@hotwired/stimulus";
import { DirectUpload } from "@rails/activestorage";
import { parse } from "exifr";
import { fireToast } from "../helpers/toast";

interface ImageMetadata {
  taken_at: string | undefined;
  latitude: number | undefined;
  longitude: number | undefined;
}

export default class extends Controller<HTMLFormElement> {
  static readonly values = {
    startingIndex: {
      type: Number,
      default: Date.now(),
    },
  };

  declare readonly startingIndexValue: number;

  private readonly _preventDefaultSubmiting = (e: SubmitEvent) => {
    e.preventDefault();

    this.submit();
  };
  private _imageIndex = 0;

  connect(): void {
    this._imageIndex = this.startingIndexValue;
    this.element.addEventListener("submit", this._preventDefaultSubmiting);
  }

  disconnect(): void {
    this.element.removeEventListener("submit", this._preventDefaultSubmiting);
  }

  async submit() {
    const filesInputs = this.element.querySelectorAll<HTMLInputElement>(
      "[name='member_inspection[member_inspection_image_files][]']"
    );
    filesInputs.forEach((input) => {
      input.disabled = true;
    });

    const filesInput = Array.from(filesInputs).find(
      (input) => input.type == "file"
    );
    if (!filesInput) {
      throw new Error("Images files input not found.");
    }

    const files = filesInput.files;
    if (!files || files.length === 0) {
      this.element.submit();
      return;
    }

    try {
      const uploadPromises = Array.from(files).map((file) =>
        this.uploadFile(file)
      );

      await Promise.all(uploadPromises);

      this.element.submit();
    } catch (error) {
      console.error("An error occurred during upload:", error);
      fireToast(
        "There was an error uploading the files. Please try again.",
        "danger"
      );
    }
  }

  async uploadFile(file: File) {
    const metadata = await this.extractMetadata(file);
    const signedId = await this.directUpload(file);

    this.createNestedHiddenFields(signedId, metadata);
  }

  async extractMetadata(file: File) {
    try {
      const exifData = await parse(file);

      return {
        taken_at: exifData?.DateTimeOriginal,
        latitude: exifData?.latitude,
        longitude: exifData?.longitude,
      };
    } catch (error) {
      console.warn("Could not read EXIF data for file:", file.name, error);
      return {
        taken_at: undefined,
        latitude: undefined,
        longitude: undefined,
      };
    }
  }

  directUpload(file: File) {
    const uploader = new DirectUpload(
      file,
      "/rails/active_storage/direct_uploads"
    );
    return new Promise<string>((resolve, reject) => {
      uploader.create((error, blob) => {
        if (error) {
          reject(error);
          return;
        }

        if (!blob) {
          reject(new Error("Blob is undefined."));
          return;
        }
        resolve(blob.signed_id);
      });
    });
  }

  private createNestedHiddenFields(
    signedId: string,
    metadata: ImageMetadata
  ): void {
    this._imageIndex += 1;
    const fields: Record<string, any> = {
      image_signed_id: signedId,
      taken_at: metadata.taken_at,
      latitude: metadata.latitude,
      longitude: metadata.longitude,
    };

    for (const [key, value] of Object.entries(fields)) {
      if (value !== undefined && value !== null) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = `member_inspection[member_inspection_images_attributes][${this._imageIndex}][${key}]`;
        input.value = value;
        this.element.appendChild(input);
      }
    }
  }
}
